import {
  Dispatch,
  Reducer,
  ReducerAction,
  ReducerState,
  useEffect,
  useReducer,
} from 'react';
import { StorageType } from '../../utilities/constants/common';
import { extend } from '../../utilities/helpers/common';
import {
  getStorage,
  isStorageAvailable,
} from '../../utilities/helpers/storage';
import { logError } from '../../utilities/helpers/errors';

interface IHookProps<R extends Reducer<any, any>> {
  reducer: R;
  initialState: ReducerState<R>;
  storageType?: StorageType;
  keysList?: string[];
}

export const usePersistedReducer = <R extends Reducer<any, any>>({
  reducer,
  initialState,
  storageType = StorageType.LOCAL,
  keysList = Object.keys(initialState),
}: IHookProps<R>): [ReducerState<R>, Dispatch<ReducerAction<R>>] => {
  const storage = isStorageAvailable(storageType) && getStorage(storageType);

  const initializer = (initialValue = initialState) => {
    try {
      let value = initialValue;

      if (storage) {
        keysList.forEach((key) => {
          const storageItem = storage.getItem(key);

          if (storageItem) {
            value = extend(value, {
              [key]: JSON.parse(storageItem) || initialValue[key],
            });
          }
        });
      }

      return value;
    } catch (error) {
      logError(error);
      return initialValue;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState, initializer);

  useEffect(() => {
    try {
      if (storage) {
        keysList.forEach((key) => {
          storage.setItem(key, JSON.stringify(state[key]));
        });
      }
    } catch (error) {
      logError(error);
    }
  }, [keysList, state, storage]);

  return [state, dispatch];
};
